import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SpaceBackground from '../components/SpaceBackground';
import { getProjectBySlug } from '../data/projects';
import { 
  ArrowLeft, 
  CheckCircle2,
  Target,
  Lightbulb,
  Users
} from 'lucide-react';

const ProjectDetail = () => {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  const styles = {
    page: {
      minHeight: '100vh',
      position: 'relative',
      overflowX: 'hidden'
    },
    content: {
      position: 'relative',
      zIndex: 1,
      width: '100%'
    },
    backButton: {
      position: 'fixed',
      top: isMobile ? '16px' : '24px',
      left: isMobile ? '16px' : '48px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      color: '#B8B0C8',
      textDecoration: 'none',
      fontSize: isMobile ? '0.85rem' : '0.95rem',
      fontWeight: 500,
      padding: isMobile ? '10px 16px' : '12px 20px',
      background: 'rgba(10, 8, 24, 0.85)',
      backdropFilter: 'blur(16px)',
      borderRadius: '100px',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      transition: 'all 0.3s ease',
      zIndex: 100
    },
    // Main container with subtle overlay
    mainContainer: {
      maxWidth: '1100px',
      margin: '0 auto',
      padding: isMobile ? '80px 16px 0' : '120px 48px 0',
      position: 'relative'
    },
    contentWrapper: {
      background: 'rgba(12, 10, 28, 0.75)',
      backdropFilter: 'blur(20px)',
      borderRadius: isMobile ? '16px' : '24px',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      overflow: 'hidden',
      marginBottom: '40px'
    },
    // Hero header with background image
    heroHeader: {
      position: 'relative',
      padding: isMobile ? '24px 20px' : '48px 56px',
      minHeight: isMobile ? '220px' : '280px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end'
    },
    heroImageBg: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden'
    },
    heroGradient: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(180deg, rgba(12, 10, 28, 0.4) 0%, rgba(12, 10, 28, 0.7) 50%, rgba(12, 10, 28, 0.95) 100%)'
    },
    heroContent: {
      position: 'relative',
      zIndex: 2
    },
    // Hero content inside wrapper
    badges: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '20px',
      flexWrap: 'wrap'
    },
    statusBadge: {
      padding: '6px 14px',
      background: 'rgba(52, 211, 153, 0.15)',
      border: '1px solid rgba(52, 211, 153, 0.3)',
      borderRadius: '100px',
      fontSize: '0.8rem',
      fontWeight: 600,
      color: '#34D399',
      letterSpacing: '0.02em',
      backdropFilter: 'blur(8px)'
    },
    industryBadge: {
      padding: '6px 14px',
      background: 'rgba(124, 92, 255, 0.15)',
      border: '1px solid rgba(124, 92, 255, 0.3)',
      borderRadius: '100px',
      fontSize: '0.8rem',
      fontWeight: 600,
      color: '#CBB8FF',
      letterSpacing: '0.02em',
      backdropFilter: 'blur(8px)'
    },
    title: {
      fontSize: 'clamp(2rem, 4.5vw, 2.75rem)',
      fontWeight: 800,
      color: '#FFFFFF',
      margin: '0 0 16px 0',
      letterSpacing: '-0.03em',
      lineHeight: 1.15,
      textShadow: '0 2px 20px rgba(0,0,0,0.5)'
    },
    teaser: {
      fontSize: isMobile ? '0.95rem' : '1.1rem',
      color: '#D0C8E0',
      lineHeight: 1.7,
      margin: 0,
      textAlign: 'left',
      textShadow: '0 1px 10px rgba(0,0,0,0.4)'
    },
    // Body content area
    bodyContent: {
      padding: isMobile ? '24px 20px 32px' : '48px 56px 56px'
    },
    // Divider
    divider: {
      height: '1px',
      background: 'linear-gradient(90deg, rgba(124, 92, 255, 0.3) 0%, rgba(255, 255, 255, 0.08) 50%, rgba(124, 92, 255, 0.3) 100%)',
      margin: '40px 0'
    },
    // Section styling
    section: {
      marginBottom: '40px'
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      marginBottom: '16px'
    },
    sectionIcon: {
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    sectionTitle: {
      fontSize: '1.15rem',
      fontWeight: 700,
      color: '#FFFFFF',
      margin: 0,
      letterSpacing: '-0.01em'
    },
    sectionText: {
      fontSize: isMobile ? '0.95rem' : '1rem',
      lineHeight: 1.85,
      color: '#B8B0C8',
      margin: 0,
      whiteSpace: 'pre-line',
      paddingLeft: isMobile ? '0' : '54px',
      textAlign: 'left'
    },
    // Overview (no icon, larger text)
    overviewText: {
      fontSize: isMobile ? '0.95rem' : '1.05rem',
      lineHeight: 1.9,
      color: '#C8C0D8',
      textAlign: 'left'
    },
    // Key Outcomes unified block
    outcomesBlock: {
      background: 'rgba(52, 211, 153, 0.04)',
      border: '1px solid rgba(52, 211, 153, 0.1)',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '20px' : '28px 32px'
    },
    outcomesTitle: {
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: 700,
      color: '#34D399',
      margin: '0 0 20px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    outcomesList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '14px'
    },
    outcomeItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px'
    },
    outcomeText: {
      fontSize: isMobile ? '0.9rem' : '0.95rem',
      color: '#D4D0E0',
      lineHeight: 1.6,
      textAlign: 'left'
    },
    // Metadata row
    metadataRow: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: isMobile ? '16px' : '24px',
      marginTop: '16px'
    },
    metadataItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    metadataLabel: {
      fontSize: '0.8rem',
      fontWeight: 600,
      color: '#8B7FA8',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    metadataValue: {
      fontSize: '0.95rem',
      color: '#C8C0D8',
      lineHeight: 1.6,
      textAlign: 'left'
    },
    partnerTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px'
    },
    partnerTag: {
      padding: '6px 14px',
      background: 'rgba(59, 130, 246, 0.08)',
      border: '1px solid rgba(59, 130, 246, 0.15)',
      borderRadius: '8px',
      fontSize: '0.85rem',
      fontWeight: 500,
      color: '#93C5FD'
    }
  };

  return (
    <div style={styles.page} data-testid="project-detail-page">
      <SpaceBackground />

      <div style={styles.content}>
        {/* Back Button */}
        <Link to="/projects" style={styles.backButton} data-testid="back-to-projects">
          <ArrowLeft size={18} />
          <span>Back to Projects</span>
        </Link>

        {/* Main Content Container */}
        <div style={styles.mainContainer}>
          <div style={styles.contentWrapper}>
            {/* Hero Header with Background Image */}
            <div style={styles.heroHeader}>
              {/* Background Image */}
              <div style={styles.heroImageBg}>
                {project.imageSrc && (
                  <img 
                    src={project.imageSrc} 
                    alt={project.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      opacity: 0.6
                    }}
                  />
                )}
                <div style={styles.heroGradient}></div>
              </div>

              {/* Hero Content */}
              <div style={styles.heroContent}>
                <div style={styles.badges}>
                  {project.status && (
                    <span style={styles.statusBadge} data-testid="project-status">
                      {project.status}
                    </span>
                  )}
                  {project.industry && (
                    <span style={styles.industryBadge} data-testid="project-industry">
                      {project.industry}
                    </span>
                  )}
                </div>
                <h1 style={styles.title}>{project.name}</h1>
                <p style={styles.teaser}>{project.teaser}</p>
              </div>
            </div>

            {/* Body Content */}
            <div style={styles.bodyContent}>
              {/* Overview Section */}
              <section style={styles.section}>
                <p style={styles.overviewText}>{project.content.overview}</p>
              </section>

            {/* Divider */}
            <div style={styles.divider}></div>

            {/* The Challenge Section */}
            <section style={styles.section}>
              <div style={styles.sectionHeader}>
                <div style={{
                  ...styles.sectionIcon,
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#F87171'
                }}>
                  <Target size={20} />
                </div>
                <h2 style={styles.sectionTitle}>The Challenge</h2>
              </div>
              <p style={styles.sectionText}>{project.content.challenge}</p>
            </section>

            {/* Divider */}
            <div style={styles.divider}></div>

            {/* Our Solution Section */}
            <section style={styles.section}>
              <div style={styles.sectionHeader}>
                <div style={{
                  ...styles.sectionIcon,
                  background: 'rgba(124, 92, 255, 0.1)',
                  color: '#A78BFA'
                }}>
                  <Lightbulb size={20} />
                </div>
                <h2 style={styles.sectionTitle}>Our Solution</h2>
              </div>
              <p style={styles.sectionText}>{project.content.ourRole}</p>
            </section>

            {/* Divider */}
            <div style={styles.divider}></div>

            {/* Key Outcomes - Unified Block */}
            {project.content.impactHighlights && project.content.impactHighlights.length > 0 && (
              <>
                <section style={styles.section}>
                  <div style={styles.outcomesBlock}>
                    <h3 style={styles.outcomesTitle}>
                      <CheckCircle2 size={20} />
                      Key Outcomes
                    </h3>
                    <div style={styles.outcomesList}>
                      {project.content.impactHighlights.map((highlight, index) => (
                        <div key={index} style={styles.outcomeItem} data-testid={`outcome-${index}`}>
                          <CheckCircle2 size={18} style={{ color: '#34D399', flexShrink: 0, marginTop: '2px' }} />
                          <span style={styles.outcomeText}>{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                {/* Divider */}
                <div style={styles.divider}></div>
              </>
            )}

            {/* Metadata Row - Partners Only */}
            {project.content.partners && project.content.partners.length > 0 && (
              <div style={styles.metadataRow}>
                <div style={styles.metadataItem}>
                  <span style={styles.metadataLabel}>
                    <Users size={14} />
                    Partners
                  </span>
                  <div style={styles.partnerTags}>
                    {project.content.partners.map((partner, index) => (
                      <span key={index} style={styles.partnerTag} data-testid={`partner-${index}`}>
                        {partner}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="footer" data-testid="footer">
          <div className="footer-glass">
            <div className="footer-content">
              <div className="footer-grid">
                <div className="footer-brand">
                  <div className="footer-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_52aedb38-24ea-4ee4-8eda-846c0d3c3b94/artifacts/8gbuyg09_Full%20Logo%20Light%20TP%20space%20%281%29.png" 
                      alt="MoonRock R&D" 
                      style={{ height: '56px', width: 'auto', marginBottom: '14px', objectFit: 'contain' }}
                    />
                    <p className="footer-tagline" style={{ margin: 0, paddingLeft: '10px' }}>We Shape What Lasts.</p>
                  </div>
                </div>

                <div className="footer-links">
                  <h4 className="footer-heading">Quick Links</h4>
                  <ul>
                    <li><Link to="/">Home</Link></li>
                    <li><Link to="/who-we-are">Who We Are</Link></li>
                    <li><Link to="/projects">Projects</Link></li>
                    <li><Link to="/contact">Contact</Link></li>
                  </ul>
                </div>

                <div className="footer-contact">
                  <h4 className="footer-heading">Contact</h4>
                  <div className="contact-info">
                    <div className="contact-item">
                      <span>Suite 403, Bldg. 393, 18th November St, Muscat</span>
                    </div>
                    <div className="contact-item">
                      <a href="mailto:admin@moonrock.om">admin@moonrock.om</a>
                    </div>
                    <div className="contact-item">
                      <a href="tel:+96896607256">+968 9660 7256</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="footer-divider"></div>
              <div className="footer-bottom">
                <p className="footer-copyright">© 2026 MoonRock R&D. All rights reserved.</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ProjectDetail;
